import autobind from 'autobind-decorator';
import PropTypes from 'prop-types';
import { Component } from 'react';
import Notifications from 'react-notification-system-redux';
import { connect } from 'react-redux';
import { InputProps } from '../../../../common/components/form-elements/Input';
import * as CommonAuthService from '../../../../common/services/auth/';
import { AuthError } from '../../../../common/services/auth/Types';
import { SurveyFollowupInputDTO } from '../../../../common/types/dto/SurveyFollowupInputDTO';
import { ConfigProp, RenderPropWithProps, RoutePropsT, TranslationFuncProp } from '../../../../common/types/prop-types/common';
import { RootState } from '../../../base/RootReducer';
import { config } from '../../../common/';
import { AppThunkDispatch } from '../../../common/types';

export interface SurveyFollowupProps extends ConfigProp {
    name: InputProps;
    email: InputProps;
    submitErrorText: string;

    externalAuthURL: string;
    btnSurveyFollowup: {
        onClick: (event: React.MouseEvent<{}> | null) => void;
        value: string;
        disabled: boolean;
    };
}

export interface SurveyFollowupContainerProps {
    setLayoutState: (name: string, email: string) => void;
}

interface ReduxStateProps {
    isAuthenticating: boolean;
}

interface ReduxDispatchProps {
    authServiceActions: CommonAuthService.Service.ServiceActions;
    dispatch: AppThunkDispatch;
}

export interface RouterSurveyFollowupProps {
}

interface SurveyFollowupContainerState {
    name: string;
    nameValMsg: string;
    email: string;
    emailValMsg: string;
    submitErrorText: string;
}

class SurveyFollowupContainer extends Component<
    SurveyFollowupContainerProps &
    TranslationFuncProp &
    RoutePropsT<RouterSurveyFollowupProps> &
    RenderPropWithProps<SurveyFollowupProps> &
    ReduxStateProps &
    ReduxDispatchProps,
    SurveyFollowupContainerState
> {
    public static propTypes = {
        t: PropTypes.func.isRequired,
        routeProps: PropTypes.any.isRequired,
        isAuthenticating: PropTypes.bool.isRequired,
        authServiceActions: CommonAuthService.Service.ServiceActionPropTypes,
        dispatch: PropTypes.func.isRequired,
        //
        children: PropTypes.func.isRequired,
        setLayoutState: PropTypes.func.isRequired,
    };

    public readonly state: SurveyFollowupContainerState = {
        name: '',
        nameValMsg: '',
        email: '',
        emailValMsg: '',
        submitErrorText: '',
    };

    public componentWillReceiveProps(nextProps: ReduxStateProps) {
        this.handleInit(nextProps);
    }

    public componentDidMount() {
        // const routeMatch = this.props.routeProps.match.params;
        this.setState({
        });
    }

    public handleInit(nextProps: ReduxStateProps) {
        return; // NOTHING TO DO ...
    }

    @autobind
    public onFieldKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter') {
            this.onSurveyFollowup(null);
        }
    }

    public validateForm() {
        if (this.props.isAuthenticating) {
            return false;
        }
        let isValid: boolean = true;
        if (this.state.name === '') {
            this.setState({ nameValMsg: this.props.t('mandatory') });
            isValid = false;
        } else if (config.Constants.Regex.HtmlInjectionValidation.test(this.state.name)) {
            this.setState({ nameValMsg: this.props.t('signup:errors:htmlInjectionValidationRegex') });
            isValid = false;
        }
        if (this.state.email === '') {
            this.setState({ emailValMsg: this.props.t('mandatory') });
            isValid = false;
        } else if (!config.Constants.Regex.EmailValidation.test(this.state.email)) {
            this.setState({ emailValMsg: this.props.t('surveyfollowup:errors:emailValidationRegex') });
            isValid = false;
        }
        return isValid;
    }

    @autobind
    public async onSurveyFollowup(e: React.MouseEvent<{}> | null) {
        if (e) {
            e.preventDefault();
        }
        if (!this.validateForm()) {
            return;
        }
        try {
            const surveyFollowupInputDTO: SurveyFollowupInputDTO = {
                name: this.state.name,
                email: this.state.email,
            };
            await this.props.authServiceActions.surveyFollowup(surveyFollowupInputDTO);
            this.props.setLayoutState(this.state.name, this.state.email);
            this.props.routeProps.history.push(`${config.BaseURL}/surveyfollowup/confirm`);
            this.props.dispatch(Notifications.success({
                title: this.props.t('success'),
                message: this.props.t('surveyfollowup:finishedsuccess'),
                position: 'tc',
                autoDismiss: 5,
            }));
        } catch (error) {
            if (error instanceof AuthError) {
                const exceptionType: string = error.authErrorInfo.exceptionType || 'UnknownException';
                const exceptionErrorMsg: string = this.props.t(`exception:${exceptionType.split('.').pop()}`);
                this.setState({ submitErrorText: exceptionErrorMsg });
                this.props.dispatch(Notifications.error({
                    title: this.props.t('error'),
                    message: exceptionErrorMsg,
                    position: 'tc',
                    autoDismiss: 5,
                }));
            } else {
                throw error;
            }
        }
    }

    @autobind
    public onNameChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({ name: event.currentTarget.value });
        if (event.currentTarget.value !== '') {
            this.setState({ nameValMsg: '' });
        } else {
            this.setState({ nameValMsg: this.props.t('mandatory') });
        }
    }

    @autobind
    public onEmailChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({ email: event.currentTarget.value });
        if (event.currentTarget.value !== '') {
            this.setState({ emailValMsg: '' });
        } else {
            this.setState({ emailValMsg: this.props.t('mandatory') });
        }
    }

    public render() {
        const newProps: SurveyFollowupProps = {
            name: {
                inputProps: {
                    value: this.state.name,
                    onChange: this.onNameChange,
                },
                errorText: this.state.nameValMsg,
                hintText: '',
                hideElem: false,
            },
            email: {
                inputProps: {
                    value: this.state.email,
                    onChange: this.onEmailChange,
                },
                errorText: this.state.emailValMsg,
                hintText: '',
                hideElem: false,
            },
            submitErrorText: this.state.submitErrorText,
            externalAuthURL: config.ExternalAuthURL,
            btnSurveyFollowup: {
                onClick: this.onSurveyFollowup,
                value: this.props.t('surveyfollowup:surveyfollowup'),
                disabled: this.props.isAuthenticating,
            },
            config,
        };

        return this.props.children(newProps);
    }
}

const mapStateToProps = (store: RootState) => ({
    isAuthenticating: store.authState.isAuthenticating,
});

const mapDispatchToProps = (dispatch: AppThunkDispatch) => ({
    authServiceActions: CommonAuthService.Service.bindServiceActionCreators(config, dispatch),
    dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(SurveyFollowupContainer);
